import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { fromZonedInput, toZonedInput } from '@nucleus-commerce/core';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { scheduleFormSchema, type ScheduleForm } from '../schemas/post-form-schema';

const isPast = (iso: string) => new Date(iso).getTime() <= Date.now();

export interface ScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** The store's IANA zone: the time is entered on its clock. */
  timeZone: string;
  /** A scheduled post's current time, prefilled. */
  initial?: string | null;
  submitting: boolean;
  /** Receives the chosen instant as ISO; rejections are shown in the dialog. */
  onSubmit: (publishAt: string) => Promise<unknown>;
}

function Body({
  timeZone,
  initial,
  submitting,
  onSubmit,
  onClose,
}: Pick<ScheduleDialogProps, 'timeZone' | 'initial' | 'submitting' | 'onSubmit'> & {
  onClose: () => void;
}) {
  const { t } = useTranslation(['social', 'common']);
  const message = useErrorMessage();
  const form = useForm<ScheduleForm>({
    resolver: useI18nResolver(scheduleFormSchema),
    defaultValues: { publishAt: toZonedInput(initial ?? null, timeZone) },
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    const iso = fromZonedInput(values.publishAt, timeZone);
    if (!iso) {
      form.setError('publishAt', { type: 'validate', message: t('social:validation.publishAt') });
      return;
    }
    if (isPast(iso)) {
      form.setError('publishAt', {
        type: 'validate',
        message: t('social:validation.publishAtPast'),
      });
      return;
    }
    try {
      await onSubmit(iso);
      onClose();
    } catch (error) {
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="publishAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('social:schedule.field')}</FormLabel>
              <FormControl>
                <Input type="datetime-local" {...field} />
              </FormControl>
              <FormDescription>{t('social:schedule.timeZone', { timeZone })}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('social:schedule.working') : t('social:schedule.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/** Picks the instant a post (or several) goes out, on the store's clock. */
export function ScheduleDialog(props: ScheduleDialogProps) {
  const { open, onOpenChange, title, description } = props;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {open ? <Body {...props} onClose={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}
